import React, { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonIcon from "@mui/icons-material/Person";
import InfoIcon from "@mui/icons-material/Info";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/slices/authSlice";
import { useLazyGetCompanyKycStatusQuery, useSubmitKycMutation } from "../../redux/services/companyApi";
import { KycNameField } from "../KycNameField";
import { createPortal } from "react-dom";
import { getApiErrorMessage } from "../../utils/apiError";

interface KYCModalProps {
    onSuccess: () => void;
}

const KYCModal: React.FC<KYCModalProps> = ({ onSuccess }) => {
    const user = useSelector(selectUser);
    const [submitKyc, { isLoading: isSubmitting }] = useSubmitKycMutation();
    const [refreshKycStatus] = useLazyGetCompanyKycStatusQuery();

    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [showPersonalIDDropdown, setShowPersonalIDDropdown] = useState(false);
    const [kycValues, setKycValues] = useState<Record<string, string>>({
        nin: "",
        driversLicense: "",
        voterCard: "",
    });
    const [successVisible, setSuccessVisible] = useState(false);
    const [error, setError] = useState("");

    const personalIDSections = [
        { key: "nin", label: "NIN", description: "Enter your National Identification Number" },
        { key: "driversLicense", label: "Drivers License", description: "Enter your Drivers License number" },
        { key: "voterCard", label: "Voter Card", description: "Enter your Voter Card number" },
    ];

    const toggleSection = (key: string) => {
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleInputChange = (key: string, value: string) => {
        setKycValues((prev) => ({ ...prev, [key]: value }));
    };

    const handleSubmit = async () => {
        setError("");

        const companyId = user?.company?.id;
        if (!companyId) {
            setError("Company not found. Please contact support.");
            return;
        }

        const hasValue = Object.values(kycValues).some((v) => v.trim() !== "");
        if (!hasValue) {
            setError("Please provide at least one ID document.");
            return;
        }

        try {
            const formData = new FormData();

            if (kycValues.nin.trim()) {
                formData.append("document_type_name", "identity card");
                formData.append("document_type_description", "National Identity Card");
                formData.append("nin", kycValues.nin.trim());
            } else if (kycValues.driversLicense.trim()) {
                formData.append("document_type_name", "driving license");
                formData.append("document_type_description", "Driver's License");
                formData.append("license_number", kycValues.driversLicense.trim());
            } else if (kycValues.voterCard.trim()) {
                formData.append("document_type_name", "voter card");
                formData.append("document_type_description", "Voter's Identification Card");
                formData.append("voter_card_number", kycValues.voterCard.trim());
            }

            const response = await submitKyc({ companyId, data: formData }).unwrap();

            if (response?.status === "success") {
                // Re-read the company so the stored KYC status is current (approved or
                // pending) — otherwise the form comes back on the next page or refresh.
                await refreshKycStatus(undefined, false).catch(() => {});
                setSuccessVisible(true);
            } else {
                setError(response?.message || "KYC submission failed. Please try again.");
            }
        } catch (err: any) {
            setError(getApiErrorMessage(err, "An error occurred. Please try again."));
        }
    };

    const handleSuccessClose = () => {
        setSuccessVisible(false);
        onSuccess();
    };

    return createPortal(
        <>
            {/*  Blocking overlay — no close button, cannot be dismissed */}
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-8 space-y-6 overflow-y-auto max-h-[90vh]">

                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-[#143887]">
                            Complete Your KYC
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            You must complete identity verification before accessing the dashboard.
                        </p>
                    </div>

                    <KycNameField />

                    <div className="border rounded-lg p-4 space-y-2">
                        <div className="flex items-center gap-2 mb-2 text-[#143887] font-semibold">
                            <PersonIcon />
                            <span>Personal ID Verification</span>
                        </div>

                        <button
                            type="button"
                            onClick={() => setShowPersonalIDDropdown(!showPersonalIDDropdown)}
                            className="w-full bg-blue-50 px-4 py-3 rounded-md flex items-center justify-between text-left"
                        >
                            <span>Select an ID</span>
                            {showPersonalIDDropdown ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </button>

                        {showPersonalIDDropdown && (
                            <div className="space-y-2">
                                {personalIDSections.map((section) => {
                                    const isOpen = expanded[section.key];
                                    return (
                                        <div key={section.key}>
                                            <button
                                                type="button"
                                                onClick={() => toggleSection(section.key)}
                                                className={`w-full px-4 py-3 rounded-md flex items-center justify-between text-left mt-2 transition duration-200 ${
                                                    isOpen
                                                        ? "bg-[#143887] text-white border border-blue-300 shadow-sm"
                                                        : "bg-gray-100"
                                                }`}
                                            >
                                                <span>{section.label}</span>
                                                {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </button>

                                            {isOpen && (
                                                <div className="p-4">
                                                    <p className="text-sm text-gray-700 mb-2">
                                                        {section.description}
                                                    </p>
                                                    <input
                                                        type="text"
                                                        placeholder={`Enter ${section.label}`}
                                                        value={kycValues[section.key] || ""}
                                                        onChange={(e) => handleInputChange(section.key, e.target.value)}
                                                        className="w-full border border-gray-300 rounded-md px-4 py-2 text-sm text-gray-600"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded-md p-4 flex items-start gap-2">
                        <InfoIcon className="mt-0.5" />
                        <div>
                            <span className="font-semibold">Info!</span>
                            <p>You must provide at least one personal ID to proceed.</p>
                        </div>
                    </div>

                    {error && (
                        <p className="text-red-500 text-sm text-center">{error}</p>
                    )}

                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="w-full bg-[#143887] text-white py-3 rounded-md hover:bg-blue-800 text-sm font-medium disabled:opacity-60"
                    >
                        {isSubmitting ? "Submitting..." : "Submit KYC"}
                    </button>
                </div>
            </div>

            {/* Success modal — only shows after successful submission */}
            {successVisible && (
                <div className="fixed inset-0 flex items-center justify-center z-[10000]">
                    <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
                    <div className="relative bg-white rounded-2xl p-8 w-96 max-w-sm shadow-2xl flex flex-col items-center">
                        <div className="bg-green-100 rounded-full p-4 mb-4">
                            <svg
                                className="w-12 h-12 text-green-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">
                            KYC Submitted!
                        </h2>
                        <p className="text-gray-600 text-center mb-6">
                            Your identity verification has been successfully submitted.
                        </p>
                        <button
                            onClick={handleSuccessClose}
                            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded-full transition-transform transform hover:scale-105 shadow-md"
                        >
                            Continue to Dashboard
                        </button>
                    </div>
                </div>
            )}
        </>,
        document.body
    );
};

export default KYCModal;